import { jsx as _jsx } from "react/jsx-runtime";
import { memo, useEffect, useMemo } from 'react';
import { OpenThermStatusFlag, OpenThermVariableName } from 'openTherm';
import { otHasError } from 'console/shared/components/banners/failure/components/fail-card/utils';
import { getBoilerStateText } from 'console/shared/content/device/heating-circuits/legacy/boiler';
import { useLegacyBoilerHeatingCircuitState } from 'console/shared/hooks/device/legacy';
import Flame from '../components/control-mode/icons/control-flame.svg';
import { BoilerHeatingCircuitContent } from './types';
import { ResponsiveIcon } from 'console/shared/components/icon/responsive';
import { useDeviceState } from 'console/shared/contexts/device/state';
import { useAllowAnimatedFlame } from 'console/shared/hooks/helpers';
import { AnimatedFlame } from 'console/shared/components/features/animated-icons/flame';
import ProFlame from '../components/control-mode/icons/pro-control-flame.svg';
import { getLegacyHeatingCircuitThermometers, getLegacyHeatingCircuitThermometersConfig, getLegacyRelayBoilerCurrentTemperature, } from 'shared/content/device/heating-circuits/legacy/utils';
import { RelayIndicator } from 'shared/components/panels/heating-circuits/heating-circuit/components/status-line/components/icons/relay';
export const LegacyBoilerHeatingCircuitProvider = memo(function LegacyBoilerHeatingCircuitProvider(props) {
    const { setOpenThermStates, boiler_heating_circuit_config, children } = props;
    const { id, name, boiler_model, boiler_type, not_available_ot_flags, is_relay } = boiler_heating_circuit_config;
    const { thermostat_target_temps, open_therm_state, last_boiler_state, thermometers, } = useLegacyBoilerHeatingCircuitState(id);
    const { is_service_mode } = useDeviceState();
    const thermometers_config = thermometers
        ? getLegacyHeatingCircuitThermometersConfig(id, thermometers)
        : {};
    const default_thermometers = useMemo(() => getLegacyHeatingCircuitThermometers(thermometers_config, thermometers), [thermometers_config, thermometers]);
    const cascades = useMemo(() => Object.values(thermostat_target_temps || {}).filter((spec) => spec.cascade), [thermostat_target_temps]);
    const is_working = useMemo(() => open_therm_state?.s?.includes(OpenThermStatusFlag.fl) ?? false, [open_therm_state]);
    const ot_boiler_state = useMemo(() => ({
        id,
        name,
        ot_state: open_therm_state,
        ba_status: null,
    }), [open_therm_state, id, name]);
    useEffect(() => {
        if (open_therm_state) {
            setOpenThermStates((states) => {
                if (states.some((state) => state.id == id)) {
                    return states.map((state) => {
                        if (state.id == id) {
                            return {
                                id,
                                boiler_type,
                                not_available_ot_flags,
                                ot_boiler_state,
                            };
                        }
                        else {
                            return state;
                        }
                    });
                }
                return [
                    ...states,
                    {
                        id,
                        boiler_type,
                        not_available_ot_flags,
                        ot_boiler_state,
                    },
                ];
            });
        }
    }, [open_therm_state, id, boiler_type, not_available_ot_flags]);
    const setpoint_temperature = useMemo(() => open_therm_state[OpenThermVariableName.cs], [open_therm_state[OpenThermVariableName.cs]]);
    const is_fail = useMemo(() => last_boiler_state?.fail ||
        (!is_relay && otHasError(boiler_model, open_therm_state, null)), [last_boiler_state?.fail, boiler_model, open_therm_state, is_relay]);
    const boiler_state = useMemo(() => getBoilerStateText(is_fail, is_working, cascades[id - 1]?.cascade), [is_fail, is_working, cascades]);
    const current_temperature = useMemo(() => is_relay
        ? getLegacyRelayBoilerCurrentTemperature(default_thermometers)
        : last_boiler_state?.ot?.bt ??
            open_therm_state[OpenThermVariableName.bt], [last_boiler_state?.ot?.bt, is_relay, default_thermometers]);
    const control_mode = useMemo(() => (is_working ? 'ot-heat' : 'ot'), [is_working]);
    const animated_icon = useAllowAnimatedFlame();
    const control_icon = useMemo(() => {
        if (is_relay) {
            return _jsx(RelayIndicator, {});
        }
        if (animated_icon && is_working) {
            return _jsx(AnimatedFlame, {});
        }
        if (animated_icon) {
            return _jsx(ProFlame, {});
        }
        return (_jsx(ResponsiveIcon, { Icon: Flame, color: is_fail && is_service_mode
                ? 'inherit'
                : is_working
                    ? 'error'
                    : 'action', "data-tooltip": "control-icon" }));
    }, [is_fail, is_working, is_service_mode, is_relay]);
    const boiler_heating_cirucit_state = useMemo(() => ({
        is_working,
        current_temperature,
        setpoint_temperature,
        boiler_state,
        is_fail,
        ot_boiler_state,
        control_icon,
        control_mode,
    }), [
        is_working,
        current_temperature,
        setpoint_temperature,
        boiler_state,
        is_fail,
        ot_boiler_state,
        control_icon,
        control_mode,
    ]);
    const boiler_heating_circuit = useMemo(() => ({ ...boiler_heating_cirucit_state, ...boiler_heating_circuit_config }), [boiler_heating_cirucit_state, boiler_heating_circuit_config]);
    return (_jsx(BoilerHeatingCircuitContent.Provider, { value: boiler_heating_circuit, children: children }));
});
